/* eslint-disable no-nested-ternary */
import React, { useMemo, useEffect, useRef, useState, forwardRef } from 'react'
import { message as $message } from 'antd'
import { sortableContainer, sortableElement } from 'react-sortable-hoc'
import arrayMove from 'array-move'
import classNames from 'classnames'

import { wrapperGallery } from '@/components/Gallery'
import styles from './SpareList.less'
import previewStyles from './Preview.less'

import Preview from './Preview'
import Single from './Single'
import Btn from './Btn'

import { defaultFailurePic, transRulesToList, valueFillBlanks, transDataToValue } from './helper'

const defaultCropRecSizes = [
    {
        label: '800x800',
        value: 1 / 1,
        fixedRatio: true
    }
]

const Container = wrapperGallery(
    forwardRef(({ className, children, id }, ref) => {
        return (
            <div ref={ref} id={id} className={classNames(styles.container, className)}>
                {children}
            </div>
        )
    })
)

const SortableContainer = sortableContainer(props => {
    return <Container {...props} />
})
const SortableElement = sortableElement(({ children }) => children)

// 组件错误，先临时处理下
const SpareList = forwardRef(
    ({
        id,
        children,
        value,
        onChange: outputChange,
        mainNum,
        minNum,
        maxNum,
        thumbnail,
        autoPreview,
        showCopy,
        showAdd,
        disabled,
        draggable,
        className,
        style,
        itemClassName,
        cropRecSizes,
        defaultImg,
        disMaterialLib,
        form,
        cloudDiskFolderId,
        imageEditable,
        imageLabel,
        singleValidate,
        ...restProps
    }) => {
        const onChange = value => {
            outputChange(
                transRulesToList({
                    list: value,
                    mainNum,
                    maxNum
                })
            )
        }

        const $this = useRef({
            value: []
        })

        const [moving, setMoving] = useState(false)

        $this.current.value = value || []

        const sizeStyle = { width: `${thumbnail.width}px`, height: `${thumbnail.height}px` }

        const handleSortEnd = ({ oldIndex, newIndex }) => {
            const newValue = arrayMove(value, oldIndex, newIndex)

            onChange(newValue)

            setMoving(false)
        }

        const buttons = useMemo(() => {
            let len = 0

            if (typeof minNum !== 'undefined') {
                len = Math.max(minNum - ($this.current.value ? $this.current.value.length : 0), 1)
            }

            if (showAdd) {
                if (typeof maxNum !== 'undefined') {
                    len = Math.min(len, maxNum - ($this.current.value ? $this.current.value.length : 0))
                }
            } else {
                if ($this.current.value.length <= 0) {
                    len = 1
                } else {
                    len = 0
                }
            }

            if (len >= 0) {
                return Array(len).fill(true)
            }

            return []
        }, [showAdd, maxNum, minNum, $this.current.value])

        const handleDelete = target => {
            onChange($this.current.value.filter(v => v.uid !== target.uid))
        }

        // 图片加载失败时替换为默认图片 status置为 failurePic
        const handleFailurePic = target => {
            target.status = 'failurePic'
            target.url = defaultImg

            const newList = $this.current.value.map(v => {
                return v.uid !== target.uid ? v : { ...v, status: 'failurePic', url: defaultImg }
            })
            onChange(newList)
        }

        // 图片裁剪成功后替换图片
        const handleCropPic = target => {
            const newList = $this.current.value.map(v => {
                return v.uid !== target.uid ? v : { ...v, status: 'done', url: target?.url }
            })
            onChange(newList)
        }

        // 图片编辑成功后替换图片
        const handleEditPic = target => {
            const newList = $this.current.value.map(v => {
                return v.uid !== target.uid ? v : { ...v, status: 'done', url: target?.url }
            })
            onChange(newList)
        }

        // 图片复制成功后增加图片
        const handleCopyPic = target => {
            const { status, url } = target
            let newList = Array.isArray($this.current.value) ? $this.current.value : []

            const len = newList.length

            if (typeof maxNum !== 'undefined' && newList.length >= maxNum) {
                $message.error(`图片已满${maxNum}张，无法复制`)
                return
            }

            const insert = () => {
                const hasEmptyLocation = newList.some(v => v?.isBtn === true)
                if (hasEmptyLocation) {
                    for (let i = 0; i < len; i++) {
                        const item = newList[i]
                        if (item.isBtn) {
                            newList[i] = { ...item, status, url, isBtn: undefined }
                            break
                        }
                    }
                } else {
                    newList.push({
                        ...target,
                        ...transDataToValue(url)
                    })
                }
            }
            insert()
            onChange([...newList])
        }

        // 主图位置图片处理
        const handleSingleChange = (items, index) => {
            const newValue = valueFillBlanks({ value: $this.current.value, items, index })
            onChange(newValue)
        }

        useEffect(() => {
            return () => {
                const { value } = $this.current

                if (Array.isArray(value)) {
                    value.forEach(v => v && v.uploadTask && v.uploadTask.abort())
                } else {
                    value && value.uploadTask && value.uploadTask.abort()
                }
            }
        }, [])

        const btnProps = {
            value: $this.current.value,
            onChange,
            minNum,
            maxNum,
            thumbnail,
            autoPreview,
            disabled,
            disMaterialLib,
            cloudDiskFolderId,
            ...restProps
        }

        const singleProps = {
            thumbnail,
            autoPreview,
            disabled,
            disMaterialLib,
            cloudDiskFolderId,
            ...restProps
        }

        // console.log('singleValidate', {
        //     singleValidate,
        //     value: $this.current.value
        // })

        return (
            <SortableContainer
                id={id}
                axis="xy"
                useDragHandle
                className={className}
                style={style}
                shouldCancelStart={() => disabled}
                helperClass={previewStyles.sortHelper}
                onSortStart={() => setMoving(true)}
                onSortEnd={handleSortEnd}
            >
                {Array.isArray($this.current.value) &&
                    $this.current.value.filter(Boolean).map((item, index) => (
                        <SortableElement index={index} key={item.uid} className={itemClassName}>
                            {typeof children === 'function' ? (
                                children(item, index)
                            ) : item.isBtn ? (
                                <div
                                    className={classNames(styles.item, itemClassName, {
                                        [styles.itemMain]: item.main
                                    })}
                                >
                                    <Single
                                        form={form}
                                        style={sizeStyle}
                                        {...singleProps}
                                        singleValidate={singleValidate}
                                        onChange={value => {
                                            handleSingleChange(value, index)
                                        }}
                                    />
                                </div>
                            ) : (
                                <div
                                    className={classNames(styles.item, itemClassName, {
                                        [styles.itemMain]: item.main
                                    })}
                                >
                                    <Preview
                                        form={form}
                                        draggable={draggable}
                                        disabled={disabled}
                                        moving={moving}
                                        style={sizeStyle}
                                        thumbnail={thumbnail}
                                        showProgress={autoPreview}
                                        showCopy={showCopy}
                                        dataSource={item}
                                        cropRecSizes={cropRecSizes || defaultCropRecSizes}
                                        imageEditable={imageEditable}
                                        imageLabel={imageLabel}
                                        onDelete={handleDelete}
                                        onFailure={handleFailurePic}
                                        onCrop={handleCropPic}
                                        onCopy={handleCopyPic}
                                        onEdit={handleEditPic}
                                        defaultImg={defaultImg}
                                        singleValidate={singleValidate}
                                    />
                                </div>
                            )}
                        </SortableElement>
                    ))}

                {Array.isArray(buttons) &&
                    buttons.map((item, index) => (
                        <div key={String(index)} className={classNames(styles.item, itemClassName)}>
                            <Btn style={sizeStyle} form={form} thumbnail={thumbnail} {...btnProps} />
                        </div>
                    ))}
            </SortableContainer>
        )
    }
)

SpareList.defaultProps = {
    icon: 'multiplePicture',
    value: {
        mainImage: [],
        spareImage: []
    },
    mainNum: 0,
    minNum: 0,
    autoPreview: true,
    showCopy: false,
    showAdd: true,
    multiple: true,
    disabled: false,
    draggable: true,
    disMaterialLib: false, // 展示素材库
    imageEditable: false, // 图片编辑
    cloudDiskFolderId: null, // 已经关联的素材库
    thumbnail: {
        // 缩略图配置项
        width: 84,
        height: 84,
        quality: 70,
        allowMagnify: true,
        crop: true,
        type: 'image/jpeg'
    },
    defaultImg: defaultFailurePic,
    shareSignature: false // 是否获取OSS永久文件夹签名信息
}

export default SpareList
